import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { employeeService, usersService, locationService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  UserPlus,
  UserMinus,
  Edit,
  MoreVertical,
  Building2,
  Mail,
  Phone,
  MapPin,
  Clock,
  Loader2,
  Users,
  Upload,
  Download,
  Copy,
  CheckCircle2,
  KeyRound,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const DEPARTMENTS = [
  'Engineering', 'Product', 'Design', 'Marketing', 'Sales', 
  'HR', 'Finance', 'Operations', 'Support', 'Legal'
];

export default function EmployeesPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [credentialsDialog, setCredentialsDialog] = useState({ open: false, username: '', name: '' });
  
  const [formData, setFormData] = useState({
    user_id: '',
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department: '',
    designation: '',
    manager_id: '',
    location_ids: [],
    shift_start: '09:30',
    shift_end: '18:30',
    face_image_url: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [emps, usrs, locs] = await Promise.all([
        employeeService.list(),
        usersService.list(),
        locationService.list(),
      ]);
      setEmployees(emps);
      setUsers(usrs);
      setLocations(locs);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      employee_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      department: '',
      designation: '',
      manager_id: '',
      location_ids: [],
      shift_start: '09:00',
      shift_end: '18:00',
      face_image_url: '',
    });
    setEditingEmployeeId(null);
    setPhotoPreview('');
    stopCamera();
  };

  const handleDialogOpenChange = (open) => {
    if (!open) {
      resetForm();
    }
    setShowAddDialog(open);
  };

  const getAvailableUsers = () => {
    const employeeUserIds = employees.map((e) => e.user_id);
    const availableUsers = users.filter((u) => !employeeUserIds.includes(u.id));
    if (formData.user_id) {
      const currentUser = users.find((u) => u.id === formData.user_id);
      if (currentUser && !availableUsers.some((u) => u.id === currentUser.id)) {
        availableUsers.unshift(currentUser);
      }
    }
    return availableUsers;
  };

  const handleEditEmployee = (employee) => {
    setFormData({
      user_id: employee.user_id || '',
      employee_code: employee.employee_code || '',
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department: employee.department || '',
      designation: employee.designation || '',
      manager_id: employee.manager_id || '',
      location_ids: employee.location_ids || [],
      shift_start: employee.shift_start || '09:30',
      shift_end: employee.shift_end || '18:30',
      face_image_url: employee.face_image_url || '',
    });
    setEditingEmployeeId(employee.id);
    setPhotoPreview(employee.face_image_url || '');
    setShowAddDialog(true);
  };

  const startCamera = async () => {
    try {
      setCameraStream(null);
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
      });
      setCameraStream(stream);
    } catch (error) {
      setShowCamera(false);
      toast.error('Unable to access camera: ' + (error?.message || 'permission denied'));
    }
  };

  useEffect(() => {
    if (!showCamera || !cameraStream || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = cameraStream;
    video.muted = true;
    video.playsInline = true;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        toast.error('Unable to start camera preview');
      });
    }
  }, [showCamera, cameraStream]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video.videoWidth || !video.videoHeight) {
      toast.error('Camera not ready yet. Please try again.');
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setFormData({ ...formData, face_image_url: imageData });
    setPhotoPreview(imageData);
    stopCamera();
  };

  const handlePhotoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        setFormData({ ...formData, face_image_url: result });
        setPhotoPreview(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveEmployee = async () => {
    if (!formData.first_name || !formData.last_name || !formData.department) {
      toast.error('First name, last name, and department are required');
      return;
    }
    if (!editingEmployeeId && !formData.user_id && !formData.email) {
      toast.error('Email is required to auto-create a login account');
      return;
    }

    setAddLoading(true);
    try {
      if (editingEmployeeId) {
        await employeeService.update(editingEmployeeId, formData);
        toast.success('Employee updated successfully!');
        setShowAddDialog(false);
        resetForm();
        loadData();
      } else {
        const payload = { ...formData };
        if (!payload.user_id) delete payload.user_id;
        const data = await employeeService.create(payload);
        toast.success('Employee added successfully!');
        setShowAddDialog(false);
        resetForm();
        loadData();
        // Show credentials dialog if account was auto-created
        if (data.auto_created_username) {
          setCredentialsDialog({
            open: true,
            username: data.auto_created_username,
            name: `${formData.first_name} ${formData.last_name}`,
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    } finally {
      setAddLoading(false);
    }
  };

  const handleOffboard = async (employeeId) => {
    if (!window.confirm('Are you sure you want to offboard this employee?')) return;
    
    try {
      await employeeService.offboard(employeeId);
      toast.success('Employee offboarded successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to offboard employee');
    }
  };

  const handleImportEmployees = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImportLoading(true);
    try {
      const result = await employeeService.import(importFile);
      setImportResults({...result, showFailed: false});
      toast.success(`Imported ${result.created_count} employees successfully`);
      if (result.failed_count > 0) {
        toast.warning(`${result.failed_count} employees failed to import`);
      }
      setShowImportDialog(false);
      setImportFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import employees');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `employee_code,first_name,last_name,email,phone,department,designation,manager_id,location_ids,shift_start,shift_end,role
EMP001,John,Doe,john.doe@company.com,+1 234 567 8900,Engineering,Software Engineer,,,09:00,18:00,employee
EMP002,Jane,Smith,jane.smith@company.com,+1 234 567 8901,Marketing,Marketing Manager,,,09:00,18:00,employee`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'employee_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = 
      emp.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employee_code.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDepartment = departmentFilter === 'all' || emp.department === departmentFilter;
    
    return matchesSearch && matchesDepartment;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Employees</h1>
          <p className="text-muted-foreground mt-1">
            Manage employee profiles and onboarding
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-employees-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import Employees
          </Button>
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-md">
              <DialogHeader>
                <DialogTitle>Bulk Import Employees</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Upload CSV or Excel File</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    data-testid="import-file-input"
                  />
                  <div className="text-sm text-muted-foreground">
                    Upload a CSV or Excel file with employee data. 
                    <Button variant="link" className="p-0 h-auto" onClick={downloadTemplate}>
                      Download template
                    </Button>
                  </div>
                </div>
                {importResults && (
                  <div className="space-y-2">
                    <Label>Import Results</Label>
                    <div className="text-sm space-y-1">
                      <p>Total rows: {importResults.total_rows}</p>
                      <p className="text-green-600">Created: {importResults.created_count}</p>
                      <p className="text-red-600">Failed: {importResults.failed_count}</p>
                      {importResults.failed_rows && importResults.failed_rows.length > 0 && (
                        <div className="mt-2">
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-red-600 text-sm underline"
                            onClick={() => setImportResults(prev => prev ? {...prev, showFailed: !prev.showFailed} : null)}
                          >
                            {importResults?.showFailed ? 'Hide' : 'View'} failed rows
                          </Button>
                          {importResults?.showFailed && (
                            <div className="mt-2 max-h-32 overflow-y-auto text-xs border rounded p-2">
                              {importResults.failed_rows.map((row, idx) => (
                                <div key={idx} className="border-b py-1 last:border-b-0">
                                  Row {row.row}: {row.reason}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleImportEmployees} disabled={importLoading || !importFile}>
                  {importLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddDialog} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button className="glow-primary" data-testid="add-employee-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEmployeeId ? 'Edit Employee' : 'Onboard New Employee'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!editingEmployeeId && (
                  <div className="md:col-span-2 bg-[#f0f9f4] border border-[#c6e8d5] rounded-lg p-3 text-sm text-[#374151]">
                    <span className="font-semibold text-[#033923]">Auto account creation:</span> Leave the field below blank — a login account will be automatically created using the employee code as username and <span className="font-mono font-semibold">Welcome@123</span> as the temporary password.
                  </div>
                )}
                <div className="space-y-2 md:col-span-2">
                  <Label>Link to Existing Account <span className="text-[#9ca3af] font-normal">(optional)</span></Label>
                  <Select
                    value={formData.user_id || 'auto'}
                    onValueChange={(v) => {
                      if (v === 'auto') {
                        setFormData({ ...formData, user_id: '' });
                        return;
                      }
                      const selectedUser = users.find(u => u.id === v);
                      setFormData({
                        ...formData,
                        user_id: v,
                        email: selectedUser?.email || formData.email
                      });
                    }}
                  >
                    <SelectTrigger data-testid="user-select">
                      <SelectValue placeholder="Auto-create new account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">— Auto-create new account —</SelectItem>
                      {getAvailableUsers().map(usr => (
                        <SelectItem key={usr.id} value={usr.id}>
                          {usr.username} ({usr.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Employee Code</Label>
                  <Input
                    value={formData.employee_code}
                    onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                    placeholder="Auto-generated if left blank"
                    data-testid="employee-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="first-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="last-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email {!formData.user_id && !editingEmployeeId && <span className="text-red-500">*</span>}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="email-input"
                  />
                  {!formData.user_id && !editingEmployeeId && (
                    <p className="text-xs text-[#6b7280]">Required — OTP login will be sent here</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                    data-testid="phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Department *</Label>
                  <Select 
                    value={formData.department}
                    onValueChange={(v) => setFormData({ ...formData, department: v })}
                  >
                    <SelectTrigger data-testid="department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    placeholder="Software Engineer"
                    data-testid="designation-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Manager</Label>
                  <Select 
                    value={formData.manager_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, manager_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="manager-select">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {employees.filter(e => e.is_active).map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Work Location</Label>
                  <Select 
                    value={formData.location_ids[0] || ''}
                    onValueChange={(v) => setFormData({ ...formData, location_ids: v ? [v] : [] })}
                  >
                    <SelectTrigger data-testid="location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.filter(l => l.is_enabled).map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} - {loc.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Employee Photo</Label>
                  <div className="space-y-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoFileChange}
                        className="file-input w-full"
                        data-testid="photo-file-input"
                      />
                      <Button type="button" variant="outline" onClick={startCamera} data-testid="start-camera-btn">
                        Capture Photo
                      </Button>
                    </div>
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Employee preview"
                        className="h-40 w-full rounded-md object-cover border border-muted"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">Upload or capture a profile photo for face verification.</p>
                    )}
                    {showCamera && (
                      <div className="space-y-2">
                        <video ref={videoRef} className="h-64 w-full rounded-md bg-black" />
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" onClick={capturePhoto} className="glow-primary" data-testid="capture-employee-photo-btn">
                            Capture
                          </Button>
                          <Button type="button" variant="outline" onClick={stopCamera}>
                            Cancel
                          </Button>
                        </div>
                        <canvas ref={canvasRef} className="hidden" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Shift Start</Label>
                  <Input
                    type="time"
                    value={formData.shift_start}
                    onChange={(e) => setFormData({ ...formData, shift_start: e.target.value })}
                    data-testid="shift-start-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Shift End</Label>
                  <Input
                    type="time"
                    value={formData.shift_end}
                    onChange={(e) => setFormData({ ...formData, shift_end: e.target.value })}
                    data-testid="shift-end-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEmployee} disabled={addLoading} data-testid="submit-employee-btn">
                {addLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingEmployeeId ? 'Update Employee' : 'Add Employee'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10 text-green-500">
                <UserPlus className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{employees.filter(e => e.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-500/10 text-red-500">
                <UserMinus className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold">{employees.filter(e => !e.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/10 text-purple-500">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Departments</p>
                <p className="text-2xl font-bold">
                  {new Set(employees.map(e => e.department)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or employee code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="department-filter">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {DEPARTMENTS.map(dept => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Employee Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table data-testid="employees-table">
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="hidden md:table-cell">Designation</TableHead>
                <TableHead className="hidden lg:table-cell">Shift</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          {emp.face_image_url && <AvatarImage src={emp.face_image_url} alt={`${emp.first_name} ${emp.last_name}`} />}
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {emp.first_name.charAt(0)}{emp.last_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                          <p className="text-sm text-muted-foreground">{emp.employee_code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{emp.department}</Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{emp.designation || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-sm">
                      {emp.shift_start} - {emp.shift_end}
                    </TableCell>
                    <TableCell>
                      <Badge className={emp.is_active ? 'badge-success' : 'badge-danger'}>
                        {emp.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`actions-${emp.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditEmployee(emp)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {emp.is_active && (
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleOffboard(emp.id)}
                            >
                              <UserMinus className="w-4 h-4 mr-2" />
                              Offboard
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Credentials dialog shown after auto-creating an employee account */}
      <Dialog open={credentialsDialog.open} onOpenChange={(o) => setCredentialsDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#033923]">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Account Created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#374151]">
              A login account has been created for <strong>{credentialsDialog.name}</strong>. Share these credentials securely:
            </p>
            <div className="bg-[#f0f9f4] border border-[#c6e8d5] rounded-lg overflow-hidden">
              <CredentialRow label="Username / Login ID" value={credentialsDialog.username} />
              <CredentialRow label="Temporary Password" value="Welcome@123" last />
            </div>
            <p className="text-xs text-[#6b7280]">
              The employee will be prompted to change their password on first login. They can also use <strong>OTP login</strong> with their email.
            </p>
          </div>
          <DialogFooter>
            <Button className="w-full bg-[#033923] text-white" onClick={() => setCredentialsDialog(d => ({ ...d, open: false }))}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredentialRow({ label, value, last }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className={`flex items-center justify-between px-4 py-3 ${!last ? 'border-b border-[#dceee4]' : ''}`}>
      <div>
        <p className="text-xs text-[#6b7280]">{label}</p>
        <p className="font-mono font-semibold text-[#033923]">{value}</p>
      </div>
      <button onClick={handleCopy} className="ml-2 p-1.5 rounded hover:bg-[#dceee4] text-[#5d746a] transition-colors">
        {copied ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}
